// src/pages/Home.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button, { ButtonSecondary } from '../components/Button';
import { Display, Heading, Body, Label, Accent } from '../components/Typography';
import Surface from '../components/Surface';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { useParallax } from '../hooks/useParallax';

// ─── Hero ───────────────────────────────────────────────────────────────────

const HeroOuter = styled.div`
  position: relative;
  overflow: hidden;
  background-color: #fff8f5;
`;

// Two decorative gradient "planes" that shift on cursor move
const GradientPlane = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  will-change: transform;
  transition: transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
`;

const PlaneA = styled(GradientPlane)`
  width: 600px;
  height: 600px;
  top: -200px;
  right: -100px;
  background: radial-gradient(circle, rgba(255, 172, 155, 0.35) 0%, transparent 70%);
`;

const PlaneB = styled(GradientPlane)`
  width: 500px;
  height: 500px;
  bottom: -150px;
  left: -80px;
  background: radial-gradient(circle, rgba(132, 245, 232, 0.2) 0%, transparent 70%);
`;

const HeroSection = styled.div`
  position: relative;
  z-index: 1;
  padding: 5rem 2rem 4rem;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3rem;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    padding: 3rem 1.5rem 2.5rem;
    text-align: center;
  }
`;

const HeroText = styled.div`
  flex: 1;
  max-width: 540px;
`;

const HeroName = styled(Display)`
  margin-bottom: 1rem;
`;

const HeroBio = styled(Body)`
  margin-bottom: 1.75rem;
  font-size: 1.0625rem;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const HeroImageWrap = styled(motion.div)`
  flex-shrink: 0;
  will-change: transform;

  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const HeroImage = styled.img`
  width: 260px;
  height: 260px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #a0412d;
  box-shadow: 0 20px 40px rgba(160, 65, 45, 0.18);

  @media (max-width: 768px) {
    width: 200px;
    height: 200px;
  }
`;

// ─── Stats Bar ────────────────────────────────────────────────────────────────

const StatsSection = styled(Surface).attrs({ tone: 'raised' })`
  padding: 2.5rem 2rem;
`;

const StatsInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  gap: 4rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.p`
  font-family: 'Epilogue', sans-serif;
  font-size: 2rem;
  font-weight: 800;
  color: #a0412d;
  line-height: 1;
  margin-bottom: 0.35rem;
`;

const StatLabel = styled(Label)`
  color: #665d58;
`;

// ─── Highlight Section ────────────────────────────────────────────────────────

const HighlightSection = styled(Surface).attrs({ tone: 'inset' })`
  padding: 4rem 2rem;
`;

const HighlightInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionLabel = styled(Label)`
  display: block;
  color: #a0412d;
  margin-bottom: 0.75rem;
`;

const HighlightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HighlightCard = styled(Surface).attrs({ tone: 'raised' })`
  padding: 1.75rem;
  border-radius: 1rem;
  box-shadow: 0 20px 40px rgba(56, 49, 45, 0.06);
`;

const CardTitle = styled.h4`
  font-family: 'Epilogue', sans-serif;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #38312d;
  margin-bottom: 0.5rem;
`;

const CardBody = styled(Body)`
  font-size: 0.9375rem;
`;

// ─── CTA Banner ────────────────────────────────────────────────────────────────

const CTASection = styled.section`
  background: linear-gradient(135deg, #a0412d 0%, #ffac9b 100%);
  padding: 4rem 2rem;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-family: 'Epilogue', sans-serif;
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 800;
  color: #fff7f6;
  margin-bottom: 0.5rem;
`;

const CTASubtitle = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.0625rem;
  color: rgba(255, 247, 246, 0.85);
  margin-bottom: 2rem;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.875rem 2rem;
  background-color: #fff7f6;
  color: #a0412d;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  border-radius: 9999px;
  text-decoration: none;
  transition: background-color 0.2s ease, transform 0.15s ease;

  &:hover {
    background-color: #ffac9b;
    color: #721f0f;
    transform: translateY(-1px);
  }
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const Home = () => {
  const { ref: heroRef, offset } = useParallax(0.6);

  return (
    <>
      {/* Hero with cursor parallax */}
      <HeroOuter ref={heroRef}>
        <PlaneA
          style={{
            transform: `translate(${offset.x * -18}px, ${offset.y * -12}px)`,
          }}
        />
        <PlaneB
          style={{
            transform: `translate(${offset.x * 12}px, ${offset.y * 18}px)`,
          }}
        />
        <HeroSection>
          <HeroText>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <HeroName>
                Sari <Accent>Angus.</Accent>
              </HeroName>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <HeroBio>
                New Zealand actress and performer. Discovered at age 8 as the face of Genesis Energy —
                one of the country's largest energy providers. Trained in singing, acting for camera,
                and stage performance since age 6.
              </HeroBio>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <HeroActions>
                <Button to="/portfolio">View Work</Button>
                <ButtonSecondary to="/contact">Get in Touch</ButtonSecondary>
              </HeroActions>
            </motion.div>
          </HeroText>

          <HeroImageWrap
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            style={{
              transform: `translate(${offset.x * 10}px, ${offset.y * 8}px)`,
            }}
          >
            <HeroImage src="/sari.png" alt="Sari Angus" />
          </HeroImageWrap>
        </HeroSection>
      </HeroOuter>

      {/* Stats — stagger each number in */}
      <StatsSection>
        <StaggerContainer
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'center',
            gap: '4rem',
            flexWrap: 'wrap',
          }}
          stagger={0.12}
        >
          {[
            { number: '4+', label: 'Years on screen' },
            { number: '6+', label: 'TVCs' },
            { number: 'National', label: 'Campaign reach' },
            { number: '10+', label: 'Stage productions' },
          ].map(({ number, label }) => (
            <StaggerItem key={label}>
              <StatItem>
                <StatNumber>{number}</StatNumber>
                <StatLabel>{label}</StatLabel>
              </StatItem>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </StatsSection>

      {/* Highlights — stagger cards */}
      <HighlightSection>
        <HighlightInner>
          <ScrollReveal variant="up">
            <SectionLabel>A Natural Talent</SectionLabel>
            <Heading>Screen. Stage. Voice.</Heading>
          </ScrollReveal>
          <StaggerContainer
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.5rem',
              marginTop: '2rem',
            }}
            stagger={0.12}
          >
            {[
              {
                title: 'Genesis Energy',
                body: 'Hero child in the national Genesis Energy campaign since 2022 — television, radio, print, and digital.',
              },
              {
                title: 'Musical Theatre',
                body: 'Trained through Showbiz Christchurch and LBF Acting for Camera. Soprano 1 vocal training with 3+ years of tuition.',
              },
              {
                title: 'Multi-disciplined',
                body: 'Netball, swimming, touch rugby, gymnastics, Kapa Haka — physical ability and cultural range that casting directors value.',
              },
            ].map(({ title, body }) => (
              <StaggerItem key={title}>
                <HighlightCard>
                  <CardTitle>{title}</CardTitle>
                  <CardBody>{body}</CardBody>
                </HighlightCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </HighlightInner>
      </HighlightSection>

      {/* CTA — fade up */}
      <ScrollReveal variant="up">
        <CTASection>
          <CTATitle>Let's create something extraordinary.</CTATitle>
          <CTASubtitle>Represented by LBF Actors, Christchurch.</CTASubtitle>
          <CTAButton href="mailto:liz@lbfactors.nz">Contact the Agent</CTAButton>
        </CTASection>
      </ScrollReveal>
    </>
  );
};

export default Home;
